import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  UserPlus, 
  MessageCircle, 
  Heart, 
  TrendingUp, 
  Activity,
  Target,
  Award,
  Calendar,
  Clock,
  Globe,
  Phone,
  Mail,
  Brain,
  Dumbbell,
  ArrowLeft
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import StatCard from './StatCard';

const SocialConnectionStats = ({ isDarkMode = true, onBackToOverview }) => {
  const socialData = {
    overallScore: 78,
    activeConnections: 90,
    qualityTime: 250,
    networkGrowth: 15,
    communicationEffectiveness: 85,
    weeklyProgress: [
      { day: 'Pzt', connections: 12, messages: 45, qualityTime: 60, social: 8 },
      { day: 'Sal', connections: 8, messages: 32, qualityTime: 40, social: 6 },
      { day: 'Çar', connections: 15, messages: 58, qualityTime: 80, social: 9 },
      { day: 'Per', connections: 10, messages: 38, qualityTime: 45, social: 7 },
      { day: 'Cum', connections: 18, messages: 72, qualityTime: 90, social: 9 },
      { day: 'Cmt', connections: 22, messages: 55, qualityTime: 120, social: 8 },
      { day: 'Paz', connections: 16, messages: 35, qualityTime: 75, social: 7 }
    ],
    communicationTypes: [
      { name: 'Yüz Yüze', value: 35, color: '#FF6B6B' },
      { name: 'Telefon', value: 25, color: '#4ECDC4' },
      { name: 'Mesajlaşma', value: 30, color: '#45B7D1' },
      { name: 'Video Arama', value: 10, color: '#96CEB4' }
    ],
    socialActivities: [
      { name: 'Arkadaş Buluşması', duration: 120, completed: true, xp: 35, participants: 4 },
      { name: 'Aile Ziyareti', duration: 180, completed: true, xp: 40, participants: 6 },
      { name: 'İş Toplantısı', duration: 60, completed: true, xp: 20, participants: 8 },
      { name: 'Akşam Yemeği', duration: 90, completed: false, xp: 25, participants: 2 }
    ],
    relationshipQualities: [
      { category: 'Aile', quality: 95, connections: 8 },
      { category: 'Yakın Arkadaş', quality: 88, connections: 12 },
      { category: 'İş Arkadaşı', quality: 75, connections: 25 },
      { category: 'Tanıdık', quality: 65, connections: 45 }
    ],
    // Cross-category summary integrating Mental and Physical stats
    crossCategorySummary: {
      mental: {
        moodScore: 85,
        stressLevel: 25,
        mindfulness: 92,
        socialAnxiety: 18
      },
      physical: {
        fitnessScore: 88,
        weeklyWorkouts: 5,
        activeMinutes: 420,
        socialSports: 2 // sports activities with others
      },
      socialImpact: {
        mentalBenefit: 23, // positive impact on mental health
        physicalBenefit: 15, // physical activities done socially
        overallWellbeing: 87
      }
    },
    achievements: [
      { name: 'Sosyal Kelebek', description: '50+ kişiyle etkileşim kur', progress: 90 },
      { name: 'İletişim Ustası', description: 'Günde 10 kaliteli konuşma', progress: 75 },
      { name: 'Ağ Kurucu', description: '5 yeni bağlantı kur', progress: 100 }
    ],
    weeklyXP: 520,
    streak: 15,
    networkInsights: {
      strongTies: 18,
      weakTies: 72,
      bridgingConnections: 5,
      influenceScore: 67
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div variants={itemVariants}>
          <button
            onClick={onBackToOverview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}
      
      {/* Social Connection Score Overview */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-blue-900 to-purple-900' : 'bg-gradient-to-br from-blue-50 to-purple-50'} shadow-lg border-2 ${isDarkMode ? 'border-blue-500/20' : 'border-blue-200'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🤝 Sosyal Bağlantı Skoru
              </h2>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Sosyal etkileşim ve bağlantı durumunuz
              </p>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold text-blue-500 mb-2`}>
                {socialData.overallScore}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                /100
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Cards */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Aktif Bağlantı" 
            icon={<Users />} 
            value={socialData.activeConnections} 
            unit="kişi" 
            progress={75}
            trend={12}
            color="#FFA400"
            isDarkMode={isDarkMode}
            index={0}
          />
          <StatCard 
            title="Kaliteli Zaman" 
            icon={<Clock />} 
            value={socialData.qualityTime} 
            unit="dk" 
            progress={68}
            trend={15}
            color="#00C2FF"
            isDarkMode={isDarkMode}
            index={1}
          />
          <StatCard 
            title="Ağ Büyümesi" 
            icon={<TrendingUp />} 
            value={socialData.networkGrowth} 
            unit="%" 
            progress={socialData.networkGrowth}
            trend={8}
            color="#FF6B6B"
            isDarkMode={isDarkMode}
            index={2}
          />
          <StatCard 
            title="İletişim Etkinliği" 
            icon={<MessageCircle />} 
            value={socialData.communicationEffectiveness} 
            unit="/100" 
            progress={socialData.communicationEffectiveness}
            trend={5}
            color="#96CEB4"
            isDarkMode={isDarkMode}
            index={3}
          />
        </div>
      </motion.div>

      {/* Cross-Category Summary - Mental & Physical Integration */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2 text-indigo-500" />
            Bütünsel Sağlık Özeti
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Mental Health Summary */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className="font-semibold mb-3 flex items-center">
                <Brain className="h-4 w-4 mr-2 text-blue-500" />
                Mental Sağlık Özeti
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Ruh Hali:</span>
                  <span className="font-medium">{socialData.crossCategorySummary.mental.moodScore}/100</span>
                </div>
                <div className="flex justify-between">
                  <span>Stres Seviyesi:</span>
                  <span className="font-medium text-green-500">Düşük</span>
                </div>
                <div className="flex justify-between">
                  <span>Sosyal Kaygı:</span>
                  <span className="font-medium text-green-500">{socialData.crossCategorySummary.mental.socialAnxiety}/100</span>
                </div>
                <div className="flex justify-between">
                  <span>Farkındalık:</span>
                  <span className="font-medium">{socialData.crossCategorySummary.mental.mindfulness}/100</span>
                </div>
              </div>
            </div>

            {/* Physical Activity Summary */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className="font-semibold mb-3 flex items-center">
                <Dumbbell className="h-4 w-4 mr-2 text-red-500" />
                Fiziksel Aktivite Özeti
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Fitness Skoru:</span>
                  <span className="font-medium">{socialData.crossCategorySummary.physical.fitnessScore}/100</span>
                </div>
                <div className="flex justify-between">
                  <span>Haftalık Antrenman:</span>
                  <span className="font-medium">{socialData.crossCategorySummary.physical.weeklyWorkouts} oturum</span>
                </div>
                <div className="flex justify-between">
                  <span>Aktif Dakika:</span>
                  <span className="font-medium">{socialData.crossCategorySummary.physical.activeMinutes} dk</span>
                </div>
                <div className="flex justify-between">
                  <span>Sosyal Spor:</span>
                  <span className="font-medium text-blue-500">{socialData.crossCategorySummary.physical.socialSports} aktivite</span>
                </div>
              </div>
            </div>

            {/* Social Impact Summary */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className="font-semibold mb-3 flex items-center">
                <Heart className="h-4 w-4 mr-2 text-pink-500" />
                Sosyal Etki Analizi
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Mental Fayda:</span>
                  <span className="font-medium text-green-500">+{socialData.crossCategorySummary.socialImpact.mentalBenefit}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Fiziksel Fayda:</span>
                  <span className="font-medium text-blue-500">+{socialData.crossCategorySummary.socialImpact.physicalBenefit}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Genel Refah:</span>
                  <span className="font-medium">{socialData.crossCategorySummary.socialImpact.overallWellbeing}/100</span>
                </div>
                <div className="text-center mt-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-500">
                    Sosyal aktiviteler refahınızı %23 artırıyor
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Weekly Progress and Communication Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Progress Chart */}
        <motion.div variants={itemVariants}>
          <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
              Haftalık Sosyal Aktivite
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={socialData.weeklyProgress}>
                  <XAxis dataKey="day" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                  <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                      borderColor: isDarkMode ? '#374151' : '#E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="connections" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    name="Bağlantılar"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="qualityTime" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name="Kaliteli Zaman"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="messages" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    name="Mesajlar"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Communication Types Distribution */}
        <motion.div variants={itemVariants}>
          <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <MessageCircle className="h-5 w-5 mr-2 text-green-500" />
              İletişim Türü Dağılımı
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={socialData.communicationTypes}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {socialData.communicationTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Today's Social Activities */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-purple-500" />
            Bugünün Sosyal Aktiviteleri
          </h3>
          <div className="space-y-3">
            {socialData.socialActivities.map((activity, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                } ${activity.completed ? 'border-l-4 border-green-500' : ''}`}
              >
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    activity.completed ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {activity.name}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {activity.duration} dakika • {activity.participants} kişi
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${
                    activity.completed ? 'text-green-500' : 'text-gray-400'
                  }`}>
                    +{activity.xp} XP
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Relationship Quality Analysis */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Heart className="h-5 w-5 mr-2 text-pink-500" />
            İlişki Kalitesi Analizi
          </h3>
          <div className="space-y-4">
            {socialData.relationshipQualities.map((relationship, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {relationship.category}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {relationship.connections} bağlantı
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${
                    relationship.quality >= 90 ? 'text-green-500' : 
                    relationship.quality >= 75 ? 'text-blue-500' : 'text-orange-500'
                  }`}>
                    {relationship.quality}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <motion.div
                    className={`h-full rounded-full ${
                      relationship.quality >= 90 ? 'bg-green-500' : 
                      relationship.quality >= 75 ? 'bg-blue-500' : 'bg-orange-500'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${relationship.quality}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Network Insights */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2 text-indigo-500" />
            Ağ Analizi & İçgörüler
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
              <div className="text-2xl font-bold text-blue-500 mb-2">
                {socialData.networkInsights.strongTies}
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Güçlü Bağlantı
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
              <div className="text-2xl font-bold text-green-500 mb-2">
                {socialData.networkInsights.weakTies}
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Zayıf Bağlantı
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
              <div className="text-2xl font-bold text-purple-500 mb-2">
                {socialData.networkInsights.bridgingConnections}
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Köprü Bağlantı
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
              <div className="text-2xl font-bold text-amber-500 mb-2">
                {socialData.networkInsights.influenceScore}
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Etki Skoru
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Progress Summary */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-blue-500 mb-2">
              {socialData.weeklyXP}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Haftalık XP
            </p>
          </div>
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-orange-500 mb-2">
              {socialData.streak}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Gün Seri
            </p>
          </div>
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-green-500 mb-2">
              {socialData.achievements.filter(a => a.progress === 100).length}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Tamamlanan Başarım
            </p>
          </div>
        </div>
      </motion.div>

      {/* Achievements Progress */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-amber-500" />
            Sosyal Bağlantı Başarımları
          </h3>
          <div className="space-y-4">
            {socialData.achievements.map((achievement, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {achievement.name}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {achievement.description}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${
                    achievement.progress === 100 ? 'text-green-500' : 'text-blue-500'
                  }`}>
                    {achievement.progress}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <motion.div
                    className={`h-full rounded-full ${
                      achievement.progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${achievement.progress}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SocialConnectionStats;
